<template>
  <IntercomLayout>
    <!-- Topbar -->
    <template #topbar>
      <IntercomTopbar
        :show-search="false"
        app-name="OnePlaceHQ"
        product-name="Billing"
        :business-name="businessName"
        :user="user"
        @notifications="handleNotifications"
        @settings="handleSettings"
        @logout="handleLogout"
      />
    </template>

    <!-- Sidebar -->
    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <!-- Main Content -->
    <div class="purchase-bills-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">Purchase Bills</h1>
          <p class="page-subtitle">Track vendor purchases and payments</p>
        </div>
        <IntercomButton variant="primary" @click="editBill = null; showCreateModal = true">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>New Purchase Bill</span>
          <kbd class="shortcut-hint">N</kbd>
        </IntercomButton>
      </div>

      <!-- Search Bar -->
      <div class="search-bar">
        <div class="search-input-wrapper">
          <svg class="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            class="search-input"
            :placeholder="$t('common.search') + '...'"
            @input="handleSearchInput"
          />
          <kbd v-if="!searchQuery" class="search-shortcut-hint">/</kbd>
          <button v-if="searchQuery" class="search-clear" @click="clearSearch">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Purchase Bills Table -->
      <IntercomCard>
        <IntercomTable
          :columns="columns"
          :data="purchaseBills"
          :loading="isLoading"
          :sort-by="sortBy"
          :sort-order="sortOrder"
          :selected-index="selectedIndex"
          @sort="handleSort"
          hoverable
          clickable
          @row-click="handleView"
        >
          <template #cell-bill_date="{ value }">
            {{ formatDate(value) }}
          </template>

          <template #cell-total_amount="{ value }">
            <div class="amount-cell">
              {{ formatCurrency(value) }}
            </div>
          </template>

          <template #cell-payment_status="{ value }">
            <IntercomBadge :variant="getPaymentBadgeVariant(value)">
              {{ formatPaymentStatus(value) }}
            </IntercomBadge>
          </template>

          <template #cell-status="{ value }">
            <IntercomBadge :variant="getStatusBadgeVariant(value)">
              {{ formatStatus(value) }}
            </IntercomBadge>
          </template>

          <template #cell-actions="{ row }">
            <div class="action-buttons">
              <IntercomButton size="sm" variant="ghost" @click.stop="handleView(row)">
                View
              </IntercomButton>
              <IntercomButton
                v-if="row.status === 'draft' || row.status === 'finalized'"
                size="sm"
                variant="ghost"
                @click.stop="handleEdit(row)"
              >
                Edit
              </IntercomButton>
              <IntercomButton
                v-if="row.status === 'draft'"
                size="sm"
                variant="ghost"
                @click.stop="handleFinalize(row)"
              >
                Finalize
              </IntercomButton>
              <IntercomButton
                v-if="row.status === 'finalized'"
                size="sm"
                variant="primary"
                @click.stop="handlePay(row)"
              >
                Pay
              </IntercomButton>
            </div>
          </template>
        </IntercomTable>
      </IntercomCard>

      <IntercomPagination
        :offset="offset"
        :total="total"
        :limit="limit"
        @update:offset="(v) => { offset = v; fetch() }"
      />

      <!-- Create/Edit Purchase Bill Modal -->
      <PurchaseBillForm
        v-model:show="showCreateModal"
        :bill="editBill"
        @success="handleFormSuccess"
      />

      <!-- Payment Modal -->
      <PurchasePaymentForm
        v-model:show="showPaymentModal"
        :bill="selectedBill"
        @success="handlePaymentSuccess"
      />
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { useConfirm } from '@shared/composables/useConfirm.js'
import { usePaginatedList } from '@shared/composables/usePaginatedList.js'
import { usePageShortcuts } from '@shared/composables/usePageShortcuts.js'
import { useBilling } from '../composables/useBilling.js'
import {
  IntercomLayout,
  IntercomTopbar,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomPagination,
  IntercomBadge,
} from '@shared/components/intercom'
import PurchaseBillForm from '../components/PurchaseBillForm.vue'
import PurchasePaymentForm from '../components/PurchasePaymentForm.vue'
import { billingNavSections } from '../config/navigation.js'
import { formatDate } from '../utils/billingUtils.js'

const { t } = useI18n()
const router = useRouter()
const { user, logout } = useAuth()
const { confirm } = useConfirm()
const { listPurchaseBills, getPurchaseBill, finalizePurchaseBill, businessName, fetchBusinessName } = useBilling()

const navSections = billingNavSections

// State
const {
  items: purchaseBills,
  total,
  loading: isLoading,
  offset,
  limit,
  fetch,
  resetAndFetch,
} = usePaginatedList((params) => listPurchaseBills(params))

const showCreateModal = ref(false)
const showPaymentModal = ref(false)
const selectedBill = ref(null)
const editBill = ref(null)
const sortBy = ref('bill_date')
const sortOrder = ref('desc')
const searchQuery = ref('')
const searchInputRef = ref(null)

// Keyboard shortcuts for list view
const { selectedIndex } = usePageShortcuts({
  items: purchaseBills,
  onNew: () => { editBill.value = null; showCreateModal.value = true },
  onOpen: (item) => handleView(item),
  onEdit: (item) => handleEdit(item),
  canEdit: (item) => item.status === 'draft' || item.status === 'finalized',
  searchRef: searchInputRef,
})

// Table columns
const columns = [
  { key: 'bill_number', title: 'Bill #', width: '130px', sortable: true },
  { key: 'vendor_name', title: 'Vendor', width: '200px' },
  { key: 'bill_date', title: 'Bill Date', width: '130px', sortable: true },
  { key: 'total_amount', title: 'Total Amount', width: '130px', align: 'right', sortable: true },
  { key: 'payment_status', title: 'Payment Status', width: '130px', sortable: true },
  { key: 'status', title: 'Status', width: '120px' },
  { key: 'actions', title: 'Actions', width: '180px', align: 'right' },
]

/**
 * Format currency in INR
 */
const formatCurrency = (val) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(val)

/**
 * Get badge variant for payment_status
 */
const getPaymentBadgeVariant = (status) => {
  switch (status) {
    case 'paid':
      return 'success'
    case 'partial':
      return 'warning'
    case 'unpaid':
    default:
      return 'default'
  }
}

/**
 * Format payment_status label
 */
const formatPaymentStatus = (status) => {
  switch (status) {
    case 'paid':
      return 'Paid'
    case 'partial':
      return 'Partial'
    case 'unpaid':
      return 'Unpaid'
    default:
      return status
  }
}

/**
 * Get badge variant for bill status
 */
const getStatusBadgeVariant = (status) => {
  switch (status) {
    case 'finalized':
      return 'success'
    case 'cancelled':
      return 'error'
    case 'draft':
    default:
      return 'default'
  }
}

/**
 * Format bill status label
 */
const formatStatus = (status) => {
  switch (status) {
    case 'draft':
      return 'Draft'
    case 'finalized':
      return 'Finalized'
    case 'cancelled':
      return 'Cancelled'
    default:
      return status
  }
}

/**
 * Handle view bill
 */
const handleView = (bill) => {
  router.push(`/purchase-bills/${bill.id}`)
}

/**
 * Handle edit draft bill — fetch full bill with items then open form
 */
const handleEdit = async (bill) => {
  try {
    const response = await getPurchaseBill(bill.id)
    editBill.value = response.purchase_bill || response
    showCreateModal.value = true
  } catch (error) {
    console.error('Failed to fetch purchase bill for editing:', error)
  }
}

/**
 * Handle finalize bill
 */
const handleFinalize = async (bill) => {
  const ok = await confirm(`Finalize bill "${bill.bill_number}"? Stock will be updated. You can still edit the bill after finalizing.`, { title: 'Finalize Purchase Bill' })
  if (!ok) {
    return
  }

  try {
    await finalizePurchaseBill(bill.id)
    await fetch()
  } catch (error) {
    console.error('Failed to finalize purchase bill:', error)
  }
}

/**
 * Handle pay bill — open payment modal
 */
const handlePay = (bill) => {
  selectedBill.value = bill
  showPaymentModal.value = true
}

/**
 * Handle form success
 */
const handleFormSuccess = async () => {
  showCreateModal.value = false
  editBill.value = null
  await fetch()
}

/**
 * Handle payment form success
 */
const handlePaymentSuccess = async () => {
  showPaymentModal.value = false
  selectedBill.value = null
  await fetch()
}

const handleSort = ({ sortBy: newSort, sortOrder: newOrder }) => {
  sortBy.value = newSort
  sortOrder.value = newOrder
  resetAndFetch({ search: searchQuery.value, sort_by: newSort, sort_order: newOrder })
}

const handleSearchInput = () => { resetAndFetch({ search: searchQuery.value, sort_by: sortBy.value, sort_order: sortOrder.value }) }
const clearSearch = () => { searchQuery.value = ''; resetAndFetch({ search: '', sort_by: sortBy.value, sort_order: sortOrder.value }) }

const handleNotifications = () => {
  console.log('Notifications')
}

const handleSettings = () => {
  router.push('/settings')
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    console.error('Logout error:', error)
    router.push('/login')
  }
}

onMounted(() => {
  fetchBusinessName()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.purchase-bills-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-8);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  letter-spacing: var(--letter-spacing-tight);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

/* Search Bar */
.search-bar { display: flex; gap: var(--space-3); margin-bottom: var(--space-6); }
.search-input-wrapper { flex: 1; position: relative; display: flex; align-items: center; }
.search-icon { position: absolute; left: 14px; color: var(--color-gray-400); pointer-events: none; }
.search-input { width: 100%; height: 44px; padding: 0 var(--space-10) 0 44px; font-size: var(--font-size-base); color: var(--color-charcoal); background: var(--color-white); border: 1px solid var(--color-gray-200); border-radius: var(--radius-lg); outline: none; transition: all var(--transition-fast); }
.search-input:focus { border-color: var(--color-blue); box-shadow: 0 0 0 3px rgba(0, 87, 255, 0.1); }
.search-clear { position: absolute; right: 12px; display: flex; align-items: center; justify-content: center; width: 24px; height: 24px; background: var(--color-gray-100); border: none; border-radius: 50%; cursor: pointer; color: var(--color-gray-500); transition: all var(--transition-fast); }
.search-clear:hover { background: var(--color-gray-200); color: var(--color-gray-700); }

.amount-cell {
  text-align: right;
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
}

.action-buttons {
  display: flex;
  gap: var(--space-2);
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .purchase-bills-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .page-title {
    font-size: 26px;
  }
}
</style>
